import { calculateVolumeWithPrices } from '../../../../common/helpers/calculate.utilities';
import type { ItemModel } from '../../../../common/models/ItemModel';
import type { SamplekitModel } from '../../../../common/models/SamplekitModel';
import type { OfferModel } from '../../../../offer/model/OfferModel';
import type { OrderModel } from '../../../model/OrderModel';

import type { LineItemComparisonCell } from './types/LineItemComparisonCell';
import { LineItemComparisonIndicator } from './types/LineItemComparisonIndicator';
import type { LineItemComparisonMatrix } from './types/LineItemComparisonMatrix';
import type { LineItemComparisonRow } from './types/LineItemComparisonRow';
import { LineItemComparisonRowType } from './types/LineItemComparisonRowType';
import type { LineItemComparisonTotalCell } from './types/LineItemComparisonTotalCell';

const getItemMatchKey = (item: Pick<ItemModel, 'index'>, fallbackIndex: number) => String(item.index ?? fallbackIndex);

const getSampleKitMatchKey = (sampleKit: Pick<SamplekitModel, 'name' | 'quantity'>) => {
    return `${sampleKit.name ?? ''}|${sampleKit.quantity ?? ''}`;
};

const getProductIndicator = (item: ItemModel) => {
    if (item.changes.productIdChanged || item.translationFailed) {
        return LineItemComparisonIndicator.DIFFERENT_PRODUCT;
    }

    if (item.baseProductId && item.productId && item.baseProductId !== item.productId) {
        return LineItemComparisonIndicator.EQUIVALENT_PRODUCT;
    }

    return undefined;
};

const getCheapestCellOfferIds = (cells: LineItemComparisonCell[]) => {
    const quotedCells = cells.filter((cell) => cell.price);

    if (!quotedCells.length) {
        return new Set<string>();
    }

    const cheapestValue = Math.min(...quotedCells.map((cell) => cell.price!.value));

    return new Set(quotedCells.filter((cell) => cell.price?.value === cheapestValue).map((cell) => cell.offerId));
};

const markCheapestCells = (cells: LineItemComparisonCell[]) => {
    const cheapestOfferIds = getCheapestCellOfferIds(cells);

    return cells.map((cell) => ({
        ...cell,
        isCheapest: cheapestOfferIds.has(cell.offerId),
    }));
};

const buildTotals = (offers: OfferModel[]): LineItemComparisonTotalCell[] => {
    return offers.map((offer) => {
        const hasValidProducts = offer.hasValidProducts();
        return {
            offerId: offer.id,
            total: hasValidProducts ? offer.total : undefined,
            ppl: hasValidProducts ? offer.ppl : undefined,
            offeredVolume: hasValidProducts ? calculateVolumeWithPrices(offer.items) : undefined,
        };
    });
};

const buildProductRow = (
    requestedItem: ItemModel,
    requestedIndex: number,
    offers: OfferModel[]
): LineItemComparisonRow => {
    const requestedMatchKey = getItemMatchKey(requestedItem, requestedIndex);
    const cells = offers.map<LineItemComparisonCell>((offer) => {
        const matchedItem = offer.items.find(
            (item, itemIndex) => getItemMatchKey(item, itemIndex) === requestedMatchKey
        );

        return {
            offerId: offer.id,
            price: matchedItem && !matchedItem.deleted ? matchedItem.price : undefined,
            total: matchedItem && !matchedItem.deleted ? matchedItem.total : undefined,
            offeredVolume: matchedItem && !matchedItem.deleted ? matchedItem.volume : undefined,
            offeredUnit: matchedItem && !matchedItem.deleted ? matchedItem.unit : undefined,
            isCheapest: false,
            indicator: matchedItem ? getProductIndicator(matchedItem) : undefined,
            productName: matchedItem?.product?.name,
        };
    });
    const cellsWithCheapest = markCheapestCells(cells);

    return {
        id: `product-${requestedMatchKey}`,
        type: LineItemComparisonRowType.PRODUCT,
        label: requestedItem.product?.name ?? '-',
        productGroup: requestedItem.product?.group,
        volume: requestedItem.volume,
        unit: requestedItem.unit,
        cells: cellsWithCheapest,
    };
};

const findSampleKitByKey = (
    sampleKits: SamplekitModel[],
    requestedSampleKit: SamplekitModel,
    requestedIndex: number
) => {
    const requestedMatchKey = getSampleKitMatchKey(requestedSampleKit);
    const matchedByKey = sampleKits.find(
        (sampleKit) => getSampleKitMatchKey(sampleKit) === requestedMatchKey && !sampleKit.deleted
    );

    return matchedByKey ?? sampleKits[requestedIndex];
};

const buildSampleKitRow = (
    requestedSampleKit: SamplekitModel,
    requestedIndex: number,
    offers: OfferModel[]
): LineItemComparisonRow => {
    const cells = offers.map<LineItemComparisonCell>((offer) => {
        const matchedSampleKit = findSampleKitByKey(offer.samplekits, requestedSampleKit, requestedIndex);

        return {
            offerId: offer.id,
            price: matchedSampleKit && !matchedSampleKit.deleted ? matchedSampleKit.value : undefined,
            total: matchedSampleKit && !matchedSampleKit.deleted ? matchedSampleKit.value : undefined,
            isCheapest: false,
        };
    });
    const cellsWithCheapest = markCheapestCells(cells);

    return {
        id: `sample-kit-${getSampleKitMatchKey(requestedSampleKit)}-${requestedIndex}`,
        type: LineItemComparisonRowType.SAMPLE_KIT,
        label: requestedSampleKit.name || '-',
        subLabel: String(requestedSampleKit.quantity || '-'),
        cells: cellsWithCheapest,
    };
};

export const buildLineItemComparisonMatrix = (order: OrderModel, offers: OfferModel[]): LineItemComparisonMatrix => {
    const rows = [
        ...order.items.filter((item) => !item.deleted).map((item, index) => buildProductRow(item, index, offers)),
        ...order.samplekits
            .filter((sampleKit) => !sampleKit.deleted)
            .map((sampleKit, index) => buildSampleKitRow(sampleKit, index, offers)),
    ];

    return {
        columns: offers.map((offer) => ({
            offerId: offer.id,
            supplierName: offer.supplier.name,
            offer,
        })),
        rows,
        totals: buildTotals(offers),
    };
};
